import type { PositionNode } from "../api/structure";

/**
 * Immutable helpers over a lazily loaded `PositionNode[]` forest (contract §5:
 * `children: null` = not loaded). Shared by the structure page and the
 * move/copy target pickers.
 */

export function findNode(nodes: PositionNode[], id: string): PositionNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const hit = findNode(n.children, id);
      if (hit) return hit;
    }
  }
  return null;
}

/** Returns a new forest with `fn` applied to the node `id` (untouched subtrees keep identity). */
export function updateNode(
  nodes: PositionNode[],
  id: string,
  fn: (node: PositionNode) => PositionNode,
): PositionNode[] {
  let changed = false;
  const out = nodes.map((n) => {
    if (n.id === id) {
      changed = true;
      return fn(n);
    }
    if (!n.children) return n;
    const kids = updateNode(n.children, id, fn);
    if (kids === n.children) return n;
    changed = true;
    return { ...n, children: kids };
  });
  return changed ? out : nodes;
}

/** Replaces the children of `id` and keeps `hasChildren` / `childCount` in sync. */
export function setChildren(nodes: PositionNode[], id: string, children: PositionNode[]): PositionNode[] {
  return updateNode(nodes, id, (n) => ({
    ...n,
    children,
    hasChildren: children.length > 0,
    childCount: children.length,
  }));
}

/**
 * Takes the slot counters ("2/13") of freshly read nodes into the forest by id; children and every
 * other field stay, and untouched subtrees keep identity.
 */
export function patchSlotCounts(nodes: PositionNode[], fresh: PositionNode[]): PositionNode[] {
  const byId = new Map(fresh.map((n) => [n.id, n]));
  const walk = (list: PositionNode[]): PositionNode[] => {
    let changed = false;
    const out = list.map((n) => {
      const kids = n.children ? walk(n.children) : n.children;
      const f = byId.get(n.id);
      const sameCounts = !f || (f.slotsBelow === n.slotsBelow && f.emptySlotsBelow === n.emptySlotsBelow);
      if (sameCounts && kids === n.children) return n;
      changed = true;
      return f
        ? { ...n, slotsBelow: f.slotsBelow, emptySlotsBelow: f.emptySlotsBelow, children: kids }
        : { ...n, children: kids };
    });
    return changed ? out : list;
  };
  return walk(nodes);
}

/** Drops the subtree rooted at `id` everywhere in the forest (used to hide a node from its own move target picker). */
export function pruneNode(nodes: PositionNode[], id: string): PositionNode[] {
  const out: PositionNode[] = [];
  for (const n of nodes) {
    if (n.id === id) continue;
    out.push(n.children ? { ...n, children: pruneNode(n.children, id) } : n);
  }
  return out;
}

/** Ids of every node on the way from a root down to (but excluding) `id`; null when `id` is not loaded. */
export function ancestorIds(nodes: PositionNode[], id: string): string[] | null {
  const walk = (list: PositionNode[], trail: string[]): string[] | null => {
    for (const n of list) {
      if (n.id === id) return trail;
      if (n.children) {
        const hit = walk(n.children, [...trail, n.id]);
        if (hit) return hit;
      }
    }
    return null;
  };
  return walk(nodes, []);
}

export type FlatPosition = { node: PositionNode; depth: number; path: { id: string; name: string; code: string | null }[] };

/** Depth-first rows in tree order: each node followed by its (loaded) children. */
export function flattenForest(forest: PositionNode[]): FlatPosition[] {
  const out: FlatPosition[] = [];
  const walk = (list: PositionNode[], depth: number, path: FlatPosition["path"]) => {
    for (const n of list) {
      out.push({ node: n, depth, path });
      if (n.children && n.children.length) walk(n.children, depth + 1, [...path, { id: n.id, name: n.name, code: n.code }]);
    }
  };
  walk(forest, 0, []);
  return out;
}

type TreeLoader = { tree: (params: { parentId?: string; depth?: number; includeDeleted?: boolean }) => Promise<PositionNode[]> };

/**
 * The whole forest (or the sub-tree under `rootId`), fully loaded: four levels per request,
 * then the still-unloaded nodes are fetched round by round until none remain.
 */
export async function loadWholeForest(api: TreeLoader, rootId?: string, includeDeleted = false): Promise<PositionNode[]> {
  const extra = includeDeleted ? { includeDeleted: true } : {};
  let forest = await api.tree({ parentId: rootId, depth: 4, ...extra });
  for (let round = 0; round < 20; round++) {
    const pending: PositionNode[] = [];
    const walk = (l: PositionNode[]) => l.forEach((n) => { if (n.hasChildren && n.children === null) pending.push(n); else if (n.children) walk(n.children); });
    walk(forest);
    if (pending.length === 0) break;
    const loaded = await Promise.all(pending.map((n) => api.tree({ parentId: n.id, depth: 4, ...extra })));
    pending.forEach((n, i) => { forest = setChildren(forest, n.id, loaded[i]); });
  }
  return forest;
}
